import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { API_BASE } from '../App'
import { 
  ArrowLeft, Plus, Save, Trash2, BookOpen, FileText, BarChart3, 
  Wind, CheckCircle, AlertCircle, X, Eye, Cloud, Loader2, Sparkles, History
} from 'lucide-react'

function AdminPanel() {
  const [apiKey, setApiKey] = useState(localStorage.getItem('meteo_admin_key') || '')
  const [message, setMessage] = useState({ type: '', text: '' })
  const [activeSection, setActiveSection] = useState('papers')
  const [papers, setPapers] = useState([])
  const [loading, setLoading] = useState(false)

  const [paperForm, setPaperForm] = useState({
    title: '', title_cn: '', authors: '', journal: '', year: 2026,
    doi: '', abstract: '', abstract_cn: '', keywords: '',
    category: '天气学', tags: '', pdf_aliyun_url: '', status: 'published'
  })

  const [translationForm, setTranslationForm] = useState({
    paper_id: '', section: 'abstract', content: '', content_cn: '', translator: 'AI'
  })

  const [vizForm, setVizForm] = useState({
    paper_id: '', title: '', chart_type: 'line', chart_config: '{}',
    data_source: '', description: ''
  })

  const [analysisForm, setAnalysisForm] = useState({
    paper_id: '', title: '', content: '', key_findings: '', meteorological_significance: ''
  })

  const categories = ['天气学', '气候学', '大气物理学', '海洋气象', '数值预报', '卫星遥感']
  const sections = [
    { value: 'abstract', label: '摘要' },
    { value: 'intro', label: '引言' },
    { value: 'methods', label: '方法' },
    { value: 'results', label: '结果' },
    { value: 'discussion', label: '讨论' },
    { value: 'conclusion', label: '结论' }
  ]

  const showMessage = (type, text) => {
    setMessage({ type, text })
    setTimeout(() => setMessage({ type: '', text: '' }), 5000)
  }

  const fetchPapers = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/papers?limit=100`)
      const data = await res.json()
      setPapers(data.data || [])
    } catch (e) {}
  }

  useEffect(() => {
    if (apiKey) fetchPapers()
  }, [apiKey])

  const handlePaperSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/api/admin/papers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          ...paperForm,
          year: parseInt(paperForm.year) || 2026,
          tags: paperForm.tags.split(',').map(s => s.trim()).filter(Boolean)
        })
      })
      const result = await res.json()
      if (result.success) {
        showMessage('success', `文献创建成功！ID: ${result.id}`)
        setPaperForm({
          title: '', title_cn: '', authors: '', journal: '', year: 2026,
          doi: '', abstract: '', abstract_cn: '', keywords: '',
          category: '天气学', tags: '', pdf_aliyun_url: '', status: 'published'
        })
        fetchPapers()
      } else {
        showMessage('error', result.error || '创建失败')
      }
    } catch (e) {
      showMessage('error', '网络错误')
    }
    setLoading(false)
  }

  const handleTranslationSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/api/admin/translations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify(translationForm)
      })
      const result = await res.json()
      if (result.success) {
        showMessage('success', '翻译添加成功！')
        setTranslationForm({ paper_id: '', section: 'abstract', content: '', content_cn: '', translator: 'AI' })
      } else {
        showMessage('error', result.error || '添加失败')
      }
    } catch (e) {
      showMessage('error', '网络错误')
    }
    setLoading(false)
  }

  const handleVizSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      let config = {}
      try {
        config = JSON.parse(vizForm.chart_config)
      } catch (e) {
        showMessage('error', '图表配置 JSON 格式错误')
        setLoading(false)
        return
      }

      const res = await fetch(`${API_BASE}/api/admin/visualizations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          ...vizForm,
          chart_config: config
        })
      })
      const result = await res.json()
      if (result.success) {
        showMessage('success', '可视化添加成功！')
        setVizForm({ paper_id: '', title: '', chart_type: 'line', chart_config: '{}', data_source: '', description: '' })
      } else {
        showMessage('error', result.error || '添加失败')
      }
    } catch (e) {
      showMessage('error', '网络错误')
    }
    setLoading(false)
  }

  const handleAnalysisSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/api/admin/analyses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify(analysisForm)
      })
      const result = await res.json()
      if (result.success) {
        showMessage('success', '分析文章添加成功！')
        setAnalysisForm({ paper_id: '', title: '', content: '', key_findings: '', meteorological_significance: '' })
      } else {
        showMessage('error', result.error || '添加失败')
      }
    } catch (e) {
      showMessage('error', '网络错误')
    }
    setLoading(false)
  }

  const deletePaper = async (id) => {
    if (!confirm('确定删除这篇文献？此操作不可恢复！')) return
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/api/admin/papers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${apiKey}` }
      })
      const result = await res.json()
      if (result.success) {
        showMessage('success', '文献已删除')
        fetchPapers()
      }
    } catch (e) {}
    setLoading(false)
  }

  const sections_menu = [
    { id: 'papers', label: '文献管理', icon: BookOpen },
    { id: 'translations', label: '翻译管理', icon: FileText },
    { id: 'visualizations', label: '可视化', icon: BarChart3 },
    { id: 'analyses', label: '分析文章', icon: Wind },
    { id: 'analyze', label: '分析任务', icon: Sparkles },
    { id: 'list', label: '文献列表', icon: Eye }
  ]

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-primary-700">
              <ArrowLeft className="w-5 h-5" />
              返回网站
            </Link>
            <h1 className="text-xl font-bold text-gray-900">管理后台</h1>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="password"
              placeholder="API Key"
              value={apiKey}
              onChange={(e) => {
                setApiKey(e.target.value)
                localStorage.setItem('meteo_admin_key', e.target.value)
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-64"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 flex gap-6">
        <div className="w-64 flex-shrink-0">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {sections_menu.map(item => {
              const Icon = item.icon
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    activeSection === item.id 
                      ? 'bg-primary-50 text-primary-700 border-r-2 border-r-primary-600' 
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="flex-1">
          {message.text && (
            <div className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
              message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              {message.text}
              <button onClick={() => setMessage({ type: '', text: '' })} className="ml-auto">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {activeSection === 'papers' && (
            <div className="card">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Plus className="w-5 h-5 text-primary-600" />
                添加新文献
              </h2>
              <form onSubmit={handlePaperSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">原文标题 *</label>
                    <input value={paperForm.title} onChange={e => setPaperForm({...paperForm, title: e.target.value})} className="input" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">中文标题</label>
                    <input value={paperForm.title_cn} onChange={e => setPaperForm({...paperForm, title_cn: e.target.value})} className="input" />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">作者</label>
                    <input value={paperForm.authors} onChange={e => setPaperForm({...paperForm, authors: e.target.value})} className="input" placeholder="Smith, J., Zhang, L." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">期刊</label>
                    <input value={paperForm.journal} onChange={e => setPaperForm({...paperForm, journal: e.target.value})} className="input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">年份</label>
                    <input type="number" value={paperForm.year} onChange={e => setPaperForm({...paperForm, year: e.target.value})} className="input" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">DOI</label>
                    <input value={paperForm.doi} onChange={e => setPaperForm({...paperForm, doi: e.target.value})} className="input" placeholder="10.xxxx/xxxxx" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">分类</label>
                    <select value={paperForm.category} onChange={e => setPaperForm({...paperForm, category: e.target.value})} className="input">
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">英文摘要</label>
                  <textarea value={paperForm.abstract} onChange={e => setPaperForm({...paperForm, abstract: e.target.value})} className="textarea" rows="4" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">中文摘要</label>
                  <textarea value={paperForm.abstract_cn} onChange={e => setPaperForm({...paperForm, abstract_cn: e.target.value})} className="textarea" rows="4" />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">关键词</label>
                    <input value={paperForm.keywords} onChange={e => setPaperForm({...paperForm, keywords: e.target.value})} className="input" placeholder="用逗号分隔" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">标签</label>
                    <input value={paperForm.tags} onChange={e => setPaperForm({...paperForm, tags: e.target.value})} className="input" placeholder="极端天气, 全球变暖, 用逗号分隔" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">阿里云盘 PDF 链接</label>
                  <input value={paperForm.pdf_aliyun_url} onChange={e => setPaperForm({...paperForm, pdf_aliyun_url: e.target.value})} className="input" placeholder="https://www.aliyundrive.com/..." />
                  <p className="text-xs text-gray-500 mt-1">将 PDF 上传到阿里云盘，开启分享，粘贴链接到这里</p>
                </div>

                <button type="submit" disabled={loading} className="btn-primary flex items-center gap-2 disabled:opacity-50">
                  <Save className="w-4 h-4" />
                  {loading ? '保存中...' : '发布文献'}
                </button>
              </form>
            </div>
          )}

          {activeSection === 'translations' && (
            <div className="card">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary-600" />
                添加翻译段落
              </h2>
              <form onSubmit={handleTranslationSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">文献 ID</label>
                    <select value={translationForm.paper_id} onChange={e => setTranslationForm({...translationForm, paper_id: e.target.value})} className="input" required>
                      <option value="">选择文献</option>
                      {papers.map(p => <option key={p.id} value={p.id}>{p.id} - {p.title_cn || p.title}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">段落</label>
                    <select value={translationForm.section} onChange={e => setTranslationForm({...translationForm, section: e.target.value})} className="input">
                      {sections.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">原文内容</label>
                  <textarea value={translationForm.content} onChange={e => setTranslationForm({...translationForm, content: e.target.value})} className="textarea" rows="6" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">中文翻译</label>
                  <textarea value={translationForm.content_cn} onChange={e => setTranslationForm({...translationForm, content_cn: e.target.value})} className="textarea" rows="6" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">译者</label>
                  <input value={translationForm.translator} onChange={e => setTranslationForm({...translationForm, translator: e.target.value})} className="input" />
                </div>
                <button type="submit" disabled={loading} className="btn-primary flex items-center gap-2 disabled:opacity-50">
                  <Save className="w-4 h-4" />
                  {loading ? '保存中...' : '添加翻译'}
                </button>
              </form>
            </div>
          )}

          {activeSection === 'visualizations' && (
            <div className="card">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary-600" />
                添加可视化图表
              </h2>
              <form onSubmit={handleVizSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">文献 ID</label>
                    <select value={vizForm.paper_id} onChange={e => setVizForm({...vizForm, paper_id: e.target.value})} className="input" required>
                      <option value="">选择文献</option>
                      {papers.map(p => <option key={p.id} value={p.id}>{p.id} - {p.title_cn || p.title}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">图表标题</label>
                    <input value={vizForm.title} onChange={e => setVizForm({...vizForm, title: e.target.value})} className="input" required />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">图表类型</label>
                    <select value={vizForm.chart_type} onChange={e => setVizForm({...vizForm, chart_type: e.target.value})} className="input">
                      <option value="line">折线图</option>
                      <option value="bar">柱状图</option>
                      <option value="scatter">散点图</option>
                      <option value="heatmap">热力图</option>
                      <option value="map">地图</option>
                      <option value="radar">雷达图</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">数据来源</label>
                    <input value={vizForm.data_source} onChange={e => setVizForm({...vizForm, data_source: e.target.value})} className="input" placeholder="ERA5, NCEP, 观测数据等" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ECharts 配置 (JSON)</label>
                  <textarea value={vizForm.chart_config} onChange={e => setVizForm({...vizForm, chart_config: e.target.value})} className="textarea font-mono text-sm" rows="12" required />
                  <p className="text-xs text-gray-500 mt-1">粘贴 ECharts option 配置 JSON</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">描述说明</label>
                  <textarea value={vizForm.description} onChange={e => setVizForm({...vizForm, description: e.target.value})} className="textarea" rows="3" />
                </div>
                <button type="submit" disabled={loading} className="btn-primary flex items-center gap-2 disabled:opacity-50">
                  <Save className="w-4 h-4" />
                  {loading ? '保存中...' : '添加可视化'}
                </button>
              </form>
            </div>
          )}

          {activeSection === 'analyses' && (
            <div className="card">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Wind className="w-5 h-5 text-primary-600" />
                添加分析文章
              </h2>
              <form onSubmit={handleAnalysisSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">文献 ID</label>
                    <select value={analysisForm.paper_id} onChange={e => setAnalysisForm({...analysisForm, paper_id: e.target.value})} className="input" required>
                      <option value="">选择文献</option>
                      {papers.map(p => <option key={p.id} value={p.id}>{p.id} - {p.title_cn || p.title}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">分析标题</label>
                    <input value={analysisForm.title} onChange={e => setAnalysisForm({...analysisForm, title: e.target.value})} className="input" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">分析内容</label>
                  <textarea value={analysisForm.content} onChange={e => setAnalysisForm({...analysisForm, content: e.target.value})} className="textarea" rows="10" placeholder="支持换行，自动转换为HTML显示" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">核心发现</label>
                  <textarea value={analysisForm.key_findings} onChange={e => setAnalysisForm({...analysisForm, key_findings: e.target.value})} className="textarea" rows="3" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">气象学意义</label>
                  <textarea value={analysisForm.meteorological_significance} onChange={e => setAnalysisForm({...analysisForm, meteorological_significance: e.target.value})} className="textarea" rows="3" />
                </div>
                <button type="submit" disabled={loading} className="btn-primary flex items-center gap-2 disabled:opacity-50">
                  <Save className="w-4 h-4" />
                  {loading ? '保存中...' : '发布分析'}
                </button>
              </form>
            </div>
          )}

          {activeSection === 'analyze' && (
            <div className="card">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                分析任务管理
              </h2>
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-amber-800 text-sm">
                  <strong>提示：</strong>分析任务由用户在「文献分析」页面提交。这里可以查看历史记录和删除失败任务。
                  AI 分析需要后端配置 <code className="bg-amber-100 px-1 rounded">[[ai]]</code> binding 才能正常运行。
                </p>
              </div>
              <AnalysisQueueList apiKey={apiKey} />
            </div>
          )}

          {activeSection === 'list' && (
            <div className="card">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Eye className="w-5 h-5 text-primary-600" />
                文献列表管理
              </h2>
              <div className="space-y-3">
                {papers.map(paper => (
                  <div key={paper.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-gray-400">#{paper.id}</span>
                        <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded">{paper.category}</span>
                      </div>
                      <p className="font-medium text-gray-900 truncate">{paper.title_cn || paper.title}</p>
                      <p className="text-sm text-gray-500">{paper.authors} · {paper.year}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link to={`/paper/${paper.id}`} target="_blank" className="p-2 text-gray-500 hover:text-primary-600 transition-colors" title="预览">
                        <Eye className="w-5 h-5" />
                      </Link>
                      <button onClick={() => deletePaper(paper.id)} className="p-2 text-gray-500 hover:text-red-600 transition-colors" title="删除">
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
                {papers.length === 0 && <p className="text-center text-gray-500 py-8">暂无文献</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminPanel

// 分析任务列表子组件
function AnalysisQueueList({ apiKey }) {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchTasks = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/api/analyze/history`)
      const data = await res.json()
      setTasks(data.data || [])
    } catch {}
    setLoading(false)
  }

  useEffect(() => { fetchTasks() }, [])

  const deleteTask = async (id) => {
    if (!confirm('确定删除此分析任务？')) return
    try {
      await fetch(`${API_BASE}/api/analyze/delete/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${apiKey}` }
      })
      fetchTasks()
    } catch {}
  }

  const statusBadge = (status) => {
    const map = {
      pending: { label: '等待中', class: 'bg-gray-100 text-gray-600' },
      processing: { label: '分析中', class: 'bg-blue-100 text-blue-700' },
      completed: { label: '已完成', class: 'bg-green-100 text-green-700' },
      failed: { label: '失败', class: 'bg-red-100 text-red-700' }
    }
    const s = map[status] || map.pending
    return <span className={`px-2 py-1 text-xs rounded-full font-medium ${s.class}`}>{s.label}</span>
  }

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>

  return (
    <div className="space-y-3">
      {tasks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <History className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          暂无分析任务
        </div>
      ) : tasks.map(task => (
        <div key={task.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-gray-400">#{task.id}</span>
              {statusBadge(task.status)}
              {task.status === 'processing' && (
                <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${task.progress}%` }} />
                </div>
              )}
            </div>
            <p className="text-sm font-medium text-gray-900 truncate">{task.file_name}</p>
            <p className="text-xs text-gray-400">{new Date(task.created_at).toLocaleString('zh-CN')}</p>
          </div>
          <div className="flex items-center gap-2">
            {task.status === 'failed' && (
              <span className="text-xs text-red-500 max-w-32 truncate" title={task.error_message}>失败</span>
            )}
            <button onClick={() => deleteTask(task.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="删除">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}
